import { Link } from "react-router-dom";
import { History, Route } from "lucide-react";

const CONFIG = {
  kept_in_play: {
    badge: "Trade-off accepted",
    badgeClass: "border-emerald-300 bg-emerald-50 text-emerald-800",
    copy: (city) =>
      "You chose to keep " +
      city +
      " in play despite living costs above your saved budget.",
  },
  unresolved: {
    badge: "Still unresolved",
    badgeClass: "border-amber-300 bg-amber-50 text-amber-900",
    copy: (city) =>
      "You weren't sure whether " +
      city +
      "'s higher living cost was worth it.",
  },
  needs_review: {
    badge: "You said this was too much",
    badgeClass: "border-rose-300 bg-rose-50 text-rose-800",
    copy: (city) =>
      city + "'s higher living cost was too much for you, but this is still shortlisted.",
  },
  observation_conflict: {
    badge: "Worth another look",
    badgeClass: "border-rose-300 bg-rose-50 text-rose-800",
    copy: () => "This choice conflicts with something you've told WUA.",
  },
  observation_tradeoff: {
    badge: "Trade-off accepted",
    badgeClass: "border-emerald-300 bg-emerald-50 text-emerald-800",
    copy: (city, effect) =>
      effect?.outweighed_by
        ? "This is not your ideal on this point, but you've said " + effect.outweighed_by.toLowerCase() + " makes it worth it."
        : "This is not your ideal on this point, but you've said you are prepared to accept it for something else you value.",
  },
};

const DECISION_LABELS = {
  accept: "Worth it",
  maybe: "Not sure yet",
  reject: "Too much",
};

function changeSummary(decisionCount) {
  const changes = Math.max(0, decisionCount - 1);
  if (changes === 1) return "You changed your mind once";
  return "You changed your mind " + changes + " times";
}

export default function ShortlistRealisationSignal({ effect, history, testId }) {
  if (!effect) return null;

  const config = CONFIG[effect.effect];
  if (!config) return null;

  const city = effect.city_name || "This city";
  const decisions = Array.isArray(history?.decisions)
    ? history.decisions.filter((row) => DECISION_LABELS[row?.response])
    : [];
  const hasChanged = decisions.length > 1;
  const visibleDecisions = decisions.slice(-4);
  const earlierCount = Math.max(0, decisions.length - visibleDecisions.length);

  return (
    <div
      data-testid={testId}
      className="mt-1.5 min-w-0 rounded-md border border-violet-200 bg-violet-50/40 px-2.5 py-2 text-[11px] text-stone-700"
    >
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        <Route className="h-3.5 w-3.5 shrink-0 text-[#4C1D95]" />
        <span
          className={
            "rounded-full border px-2 py-0.5 font-semibold " +
            config.badgeClass
          }
        >
          {config.badge}
        </span>
        <span className="min-w-0 break-words [overflow-wrap:anywhere]">
          {config.copy(city, effect)}
        </span>
        <Link
          to="/realisations"
          className="shrink-0 font-semibold text-[#4C1D95] hover:underline"
        >
          Review in Realisations →
        </Link>
      </div>

      {hasChanged && (
        <div
          data-testid={testId + "-decision-history"}
          className="mt-2 flex min-w-0 flex-wrap items-center gap-1.5 border-t border-violet-200/70 pt-2 text-[10px] text-stone-600"
        >
          <History className="h-3.5 w-3.5 shrink-0 text-[#4C1D95]" />
          <span className="font-semibold text-[#4C1D95]">
            {changeSummary(decisions.length)}:
          </span>
          {earlierCount > 0 && (
            <span className="text-stone-500">
              +{earlierCount} earlier ·
            </span>
          )}
          <span className="min-w-0 break-words [overflow-wrap:anywhere]">
            {visibleDecisions
              .map((row) => DECISION_LABELS[row.response])
              .join(" → ")}
          </span>
        </div>
      )}
    </div>
  );
}
